import React, { Fragment, ReactElement, useEffect, useState } from 'react';
import dayjs from 'dayjs';
import API from '@sesame/web-api';

import { useAuth0 } from '@auth0/auth0-react';
import { ILoadState, ISalesorderWarranty, IWarranty } from 'src/types';
import { FormInput, FormSelect } from 'src/components/Form';
import { getWarrantyCacheList } from 'src/util/resources/WarrantyCache';
import { Spinner } from 'src/components/Spinner';
import { get } from 'js-cookie';

interface IWarrantiesForSalesorderProps {
  salesorderId: number;
}
export const WarrantiesForSalesorder = ({ salesorderId }: IWarrantiesForSalesorderProps) => {
  const { isAuthenticated, isLoading, getAccessTokenSilently } = useAuth0();
  if (isLoading) {
    return <Spinner />
  }

  const [salesOrderwarrantyList, setSalesOrderWarrantyList] = useState<ISalesorderWarranty[]>([]);
  const [warrantyList, setWarrantyList] = useState<IWarranty[]>([]);
  const [displayState, setDisplayState] = useState<ILoadState>(ILoadState.NEW);

  const getSalesorderWarranties = async () => {
    if (isLoading || !isAuthenticated) {
      return;
    }

    getAccessTokenSilently().then(async accessToken => {
      const resp = await API.get(accessToken, `/api/v1/salesorder/${salesorderId}/warranty`)
        .catch(err => {
          setDisplayState(ILoadState.ERROR);
          return;
        })
      setSalesOrderWarrantyList(resp.data as ISalesorderWarranty[]);
      setDisplayState(ILoadState.READY);
    })
  }

  const remove = (salesorderWarrantyId: number) => {
    if (isLoading || !isAuthenticated) {
      return;
    }
    getAccessTokenSilently().then(async accessToken => {
      API.delete(accessToken, `/api/v1/salesorder/${salesorderId}/warranty/${salesorderWarrantyId}`, {})
        .then(() => getSalesorderWarranties());
    })
  }

  useEffect(() => {
    if (isLoading || !isAuthenticated) {
      return;
    }
    getAccessTokenSilently().then(async accessToken => {
      getWarrantyCacheList(accessToken)
        .then(list => {
          setWarrantyList(list);
          getSalesorderWarranties();
        });
    });
  }, [salesorderId, getAccessTokenSilently]);

  if (displayState == ILoadState.NEW || displayState == ILoadState.LOADING) {
    return <div>
      <h2>Warranties</h2>
    </div>
  }
  if (displayState == ILoadState.ERROR) {
    return <div>
      <h2>Warranties</h2>
      <p>Error loading warranty data</p>
    </div>
  }
  return <>
    <h2 className="uk-card-title">Warranties
      <span className="uk-align-right">
        <EditSalesorderWarranty salesorderId={salesorderId} onSave={() => getSalesorderWarranties()} />
      </span>
    </h2>
    <table className="uk-table uk-table-small">
      <thead>
        <tr>
          <th>Warranty</th>
          <th>Starts</th>
          <th>Ends</th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        {salesOrderwarrantyList.map(sw => <tr key={sw.salesorderWarrantyId}>
          <td>{warrantyList.find(w => w.warrantyId === sw.warrantyId) ?
            warrantyList.find(w => w.warrantyId === sw.warrantyId)!.name : '?'}</td>
          <td>{dayjs(sw.startDate).format('MM/DD/YYYY')}</td>
          <td>{dayjs(sw.endDate).format('MM/DD/YYYY')}</td>
          <td>{/*}<EditSalesorderWarranty
            salesorderId={salesorderId}
            salesorderWarrantyId={sw.salesorderWarrantyId}
            onSave={() => getSalesorderWarranties()} />*/}
            <a onClick={() => remove(sw.salesorderWarrantyId!)} data-uk-icon="trash" /></td>
        </tr>)}
      </tbody>
    </table>
  </>
};

interface IEditSalesorderWarrantyProps {
  salesorderId: number;
  salesorderWarrantyId?: number;
  onSave: () => void;
}

type IFormData = { [Property in keyof ISalesorderWarranty]: string; } & { [key: string]: string };
type fieldNames = keyof IFormData;

const EditSalesorderWarranty = ({ salesorderId, salesorderWarrantyId, onSave }: IEditSalesorderWarrantyProps) => {
  const { isAuthenticated, isLoading, getAccessTokenSilently } = useAuth0();
  if (isLoading) {
    return <Spinner />
  }

  const [open, setOpen] = useState(false);
  const [warrantyList, setWarrantyList] = useState<IWarranty[]>([]);
  const [formData, setFormData] = useState<IFormData>({ warrantyId: "0", startDate: '' });
  const [validation, setValidation] = useState<string[]>([]);

  useEffect(() => {
    if (isLoading || !isAuthenticated) {
      return;
    }
    getAccessTokenSilently().then( async accessToken => {
      getWarrantyCacheList(accessToken)
      .then(list => {
        setWarrantyList(list);
        const newFormData = { ...formData };
        if (newFormData.warrantyId == "0") {
          newFormData.warrantyId = '' + list[0].warrantyId;
        }
        setFormData(newFormData);
      });
    });
  }, [getAccessTokenSilently]);

  const formChange = (item: fieldNames, event: React.FormEvent<HTMLInputElement | HTMLSelectElement>) => {
    const newFormData = { ...formData }
    newFormData[item] = event.currentTarget.value;

    setFormData(newFormData);
  }

  const save = (event: React.FormEvent<HTMLElement>) => {
    event.preventDefault();

    if (isLoading || !isAuthenticated) {
      return;
    }
    const startDate = dayjs(formData.startDate).toDate()
    if (!formData.startDate.match(/\d{1,2}\/\d{1,2}\/\d{4}/) || !startDate || startDate.toString() == 'Invalid Date') {
      setValidation(['startDate']);
      return;
    }
    const newWarranty: ISalesorderWarranty = {
      salesorderWarrantyId: salesorderWarrantyId,
      warrantyId: +formData.warrantyId,
      startDate,
    }
    getAccessTokenSilently().then( accessToken => {
    API.post(accessToken, `/api/v1/salesorder/${salesorderId}/warranty`, newWarranty)
      .then(resp => {
        onSave();
        setOpen(false);
      });
    });
  }

  if (!open) {
    if (salesorderWarrantyId) {
      return <a onClick={() => setOpen(true)} data-uk-icon="pencil"></a>
    }
    return <tr>
      <td></td><td></td><td></td>
      <td><a onClick={() => setOpen(true)} className="uk-button uk-button-small uk-button-primary">Add</a></td>
    </tr>
  }
  return <tr>
    <td>
      <FormSelect
        name="warrantyId"
        label="Warranty"
        inline={true}
        data={formData}
        displayFn={() => warrantyList.map(w => { return { value: `${w.warrantyId}`, key: `${w.warrantyId}`, text: w.name } })}
        changeFn={formChange}
        validation={validation}
      />
    </td>
    <td>
      <FormInput
        name="startDate"
        label="Start Date MM/DD/YYYY"
        inline={true}
        data={formData}
        changeFn={formChange}
        validation={validation}
      />
    </td>
    <td></td>
    <td>
      <a onClick={(e) => save(e)} data-uk-icon="check" className="uk-text-success" />
      <a onClick={() => setOpen(false)} data-uk-icon="close" className="uk-text-danger" />
    </td>
  </tr>

};
